import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { message, Spin, Card, Statistic, Row, Col, Tag, Table, Button, Space, Modal } from 'antd';
import moment from 'moment';

const SchedulerControl = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [triggerModal, setTriggerModal] = useState(false);
  const [triggerType, setTriggerType] = useState('reservations');
  const [triggerLoading, setTriggerLoading] = useState(false);

  // Obtener token de autenticación
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Configuración de axios
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5002',
    headers: {
      'Authorization': `Bearer ${getToken()}`
    }
  });

  // Obtener estado del scheduler
  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/integraciones/status');
      if (response.data.success) {
        setStatus(response.data.data);
      } else {
        message.error('Error al obtener estado del scheduler');
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
      message.error('Error de conexión con el servidor');
    } finally {
      setLoading(false);
    }
  };

  // Obtener historial de sincronizaciones
  const fetchHistory = async (limit = 20) => {
    setHistoryLoading(true);
    try {
      const response = await api.get(`/api/integraciones/history?limit=${limit}`);
      if (response.data.success) {
        setHistory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      message.error('Error al obtener historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Iniciar scheduler
  const startScheduler = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/integraciones/start');
      if (response.data.success) {
        message.success('Scheduler iniciado correctamente');
        fetchStatus();
      } else {
        message.error('Error al iniciar scheduler');
      }
    } catch (error) {
      console.error('Error starting scheduler:', error);
      message.error('Error al iniciar scheduler');
    } finally {
      setLoading(false);
    }
  };

  // Detener scheduler
  const stopScheduler = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/integraciones/stop');
      if (response.data.success) {
        message.success('Scheduler detenido correctamente');
        fetchStatus();
      } else {
        message.error('Error al detener scheduler');
      }
    } catch (error) {
      console.error('Error stopping scheduler:', error);
      message.error('Error al detener scheduler');
    } finally {
      setLoading(false);
    }
  };

  // Ejecutar sincronización manual
  const triggerSync = async () => {
    setTriggerLoading(true);
    try {
      const response = await api.post('/api/integraciones/trigger-sync', {
        type: triggerType,
        options: {}
      });
      
      if (response.data.success) {
        message.success(`Sincronización ${triggerType} iniciada correctamente`);
        setTriggerModal(false);
        // Actualizar historial después de un momento
        setTimeout(() => fetchHistory(20), 2000);
      } else {
        message.error('Error al iniciar sincronización');
      }
    } catch (error) {
      console.error('Error triggering sync:', error);
      message.error('Error al iniciar sincronización');
    } finally {
      setTriggerLoading(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchStatus();
    fetchHistory(20);
    
    // Actualizar cada 30 segundos
    const interval = setInterval(() => {
      fetchStatus();
      fetchHistory(20);
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Formatear tiempo
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  // Columnas para la tabla de historial
  const historyColumns = [
    {
      title: 'Fecha',
      dataIndex: 'sync_start',
      key: 'sync_start',
      render: (text) => moment(text).format('DD/MM/YYYY HH:mm:ss'),
      sorter: (a, b) => moment(a.sync_start).unix() - moment(b.sync_start).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Operación',
      dataIndex: 'operation',
      key: 'operation',
      render: (text) => {
        const colors = {
          reservations: 'blue',
          availability: 'green',
          inventory: 'purple',
          connection: 'orange',
          cleanup: 'gray'
        };
        return <Tag color={colors[text] || 'default'}>{text}</Tag>;
      }
    },
    {
      title: 'Hotel',
      dataIndex: 'hotel_code',
      key: 'hotel_code',
      render: (text) => text || '-'
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (text) => {
        const colors = {
          started: 'processing',
          completed: 'success',
          failed: 'error'
        };
        return <Tag color={colors[text] || 'default'}>{text}</Tag>;
      }
    },
    {
      title: 'Registros',
      dataIndex: 'records_processed',
      key: 'records_processed',
      render: (text) => text || 0
    },
    {
      title: 'Duración',
      dataIndex: 'duration_seconds',
      key: 'duration_seconds',
      render: (text) => formatDuration(text)
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      key: 'error_message',
      render: (text) => text ? <Tag color="error" title={text}>Error</Tag> : '-'
    }
  ];

  return (
    <Card 
      title={
        <Space>
          <ClockCircleOutlined />
          <span>Control del Scheduler Automático</span>
          {loading && <Spin size="small" />}
        </Space>
      }
      style={{ marginTop: 20 }}
    >
      {/* Estado actual */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Estado"
              value={status?.isRunning ? 'Activo' : 'Detenido'}
              valueStyle={{ color: status?.isRunning ? '#52c41a' : '#ff4d4f' }}
              prefix={status?.isRunning ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="En progreso"
              value={status?.syncInProgress ? 'Sí' : 'No'}
              valueStyle={{ color: status?.syncInProgress ? '#faad14' : '#8c8c8c' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Última sincronización"
              value={status?.lastSyncTime ? moment(status.lastSyncTime).format('HH:mm:ss') : '-'}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Ciclos completados"
              value={status?.syncCount || 0}
              suffix={`/ ${status?.errorCount || 0} errores`}
            />
          </Card>
        </Col>
      </Row>

      {/* Configuración */}
      {status?.config && (
        <Card size="small" title="Configuración" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic 
                title="Intervalo" 
                value={`${status.config.syncInterval} minutos`}
                prefix={<ClockCircleOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="Hoteles" 
                value={status.config.hotels?.join(', ')}
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="Días hacia atrás" 
                value={status.config.lookbackDays}
                suffix="días"
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Botones de control */}
      <Space style={{ marginBottom: 24 }}>
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          onClick={startScheduler}
          disabled={status?.isRunning}
          loading={loading}
        >
          Iniciar Scheduler
        </Button>
        
        <Button
          danger
          icon={<PauseCircleOutlined />}
          onClick={stopScheduler}
          disabled={!status?.isRunning}
          loading={loading}
        >
          Detener Scheduler
        </Button>
        
        <Button
          icon={<SyncOutlined />}
          onClick={() => {
            fetchStatus();
            fetchHistory(20);
          }}
        >
          Actualizar
        </Button>
        
        <Button
          icon={<HistoryOutlined />}
          onClick={() => setShowHistory(!showHistory)}
        >
          {showHistory ? 'Ocultar Historial' : 'Mostrar Historial'}
        </Button>

        <Button
          type="dashed"
          onClick={() => setTriggerModal(true)}
        >
          Sincronización Manual
        </Button>
      </Space>

      {/* Historial de sincronizaciones */}
      {showHistory && (
        <Card 
          title="Historial de Sincronizaciones"
          size="small"
          style={{ marginTop: 16 }}
          extra={
            <Button 
              size="small" 
              onClick={() => fetchHistory(50)}
              loading={historyLoading}
            >
              Refrescar
            </Button>
          }
        >
          <Table
            columns={historyColumns}
            dataSource={history}
            rowKey="id"
            loading={historyLoading}
            size="small"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1200 }}
          />
        </Card>
      )}

      {/* Modal para sincronización manual */}
      <Modal
        title="Sincronización Manual"
        open={triggerModal}
        onCancel={() => setTriggerModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setTriggerModal(false)}>
            Cancelar
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={triggerSync}
            loading={triggerLoading}
          >
            Ejecutar
          </Button>
        ]}
      >
        <p>Selecciona el tipo de sincronización a ejecutar:</p>
        <Radio.Group 
          onChange={(e) => setTriggerType(e.target.value)} 
          value={triggerType}
          style={{ marginTop: 10 }}
        >
          <Space direction="vertical">
            <Radio value="reservations">Reservas</Radio>
            <Radio value="connection">Test de Conexión</Radio>
            <Radio value="cleanup">Limpieza de Logs</Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </Card>
  );
};

// Añadir Radio si no está importado
const Radio = ({ children, ...props }) => {
  return (
    <label style={{ display: 'block', marginBottom: 8 }}>
      <input 
        type="radio" 
        {...props} 
        style={{ marginRight: 8 }}
      />
      {children}
    </label>
  );
};

export default SchedulerControl;